import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMerlinLogoColor } from '../../hooks/useMerlinLogoColor';
import { Button } from '../UI';
import { Icon } from '../UI';
import { IMembershipPlan } from '../../types/model';
import { Card } from '../UI';
import { useNavigate } from 'react-router';
import { trackEvent } from '../../helpers/analytics';

type Feature =
  | 'ASSETS'
  | 'PROFILES'
  | 'SMART_ALERT'
  | 'WIDGET'
  | 'MARKET_WIDGET'
  | 'STRATEGIES'
  | 'COMMON'
  | '';

export const UpgradeSubscription = ({
  className = '',
  bannerClassName = '',
  plans = [],
  feature = 'COMMON',
  isOpen = false,
  onCloseClick,
  onUpgradeClick,
  viewMode = 'modal',
  textVars = {},
}: {
  viewMode: 'modal' | 'banner' | 'card';
  feature: Feature;
  className?: string;
  bannerClassName?: string;
  plans?: IMembershipPlan[];
  isOpen?: boolean;
  onCloseClick?(): void;
  onUpgradeClick?(): void;
  textVars?: {};
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [availablePlans, setAvailablePlans] = useState<string>('');

  const { setCustomColor, resetColor } = useMerlinLogoColor('#B7FF00');

  useEffect(() => {
    if (viewMode === 'modal' && isOpen) {
      setCustomColor();
      return () => {
        resetColor();
      };
    }

    if (plans && plans.length) {
      setAvailablePlans(plans.map((v: any) => v.title).join(', '));
    }
  }, [plans, isOpen]);

  const handleUpgradeClick = () => {
    trackEvent('click_upgrade');
    onCloseClick?.();
    onUpgradeClick?.();
    navigate('/subscriptions');
  };

  return viewMode === 'modal' ? (
    <>
      {!isOpen ? (
        <></>
      ) : (
        <>
          <div
            className={`
              fixed
              top-0
              left-0
              bottom-0
              right-0
              w-screen
              h-screen
              z-80
              bg-black-backdrop
              flex
              justify-center
              items-center
            `}
          >
            <div
              className={`
                max-h-11/12
                h-auto
                m-auto
                md:my-1/12
                w-11/12
                md:w-2/3
                lg:w-1/3
                xl:w-1/3
                2xl:w-1/4
                fixed
                centered
                bg-lime
                flex
                flex-col
                py-5
                rounded-xl
                ${className}
              `}
            >
              <div className={`flex items-center w-full absolute z-10`}>
                {/* close */}
                {onCloseClick && (
                  <Icon
                    svgName="close"
                    className="ml-auto cursor-pointer mr-5 dark:text-black"
                    onClick={onCloseClick}
                  />
                )}
              </div>

              <div className="flex flex-col items-center bg-primary w-full py-16 px-8 md:px-16 lg:px-32 ">
                <img alt="merlin" src="/upgradePlan.svg" className={'w-60'} />
                <h1 className="text-black text-lg font-semibold text-center mt-4">
                  {t(`upgradeSubscription.${feature}.title`, {
                    plan: availablePlans,
                    ...textVars,
                  })}
                </h1>
                <p className="mt-2 text-black text-md text-center">
                  {t(`upgradeSubscription.${feature}.subtitle`, {
                    plan: availablePlans,
                    ...textVars,
                  })}
                </p>
                <Button
                  buttonClass="w-full mt-5"
                  type="button"
                  variant="tertiary"
                  onClick={() => handleUpgradeClick()}
                >
                  {t('strategies.configureAsset.upgrade.button')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  ) : viewMode === 'banner' ? (
    <>
      {!isOpen ? (
        <></>
      ) : (
        <div className={bannerClassName}>
          <Card className={`${className} bg-primary`} title={''}>
            <div className={`flex  items-center w-full`}>
              {onCloseClick && (
                <Icon
                  svgName="close"
                  className="ml-auto cursor-pointer mr-5 dark:text-black"
                  onClick={onCloseClick}
                />
              )}
            </div>
            <div className="grid grid-cols-6 items-center">
              <img
                alt="merlin"
                src="/upgradePlan.svg"
                className={'w-25 justify-self-start px-8'}
              />
              <div className="flex flex-col items-center bg-primary col-span-4 pb-2">
                <h1 className="text-black text-xl font-semibold text-center mt-2">
                  {t(`upgradeSubscription.${feature}.title`, { ...textVars })}
                </h1>
                <p className="mt-2 text-black text-md text-center">
                  {t(`upgradeSubscription.${feature}.subtitle`, {
                    ...textVars,
                  })}
                </p>
                <Button
                  buttonClass="w-50 mt-5"
                  type="button"
                  variant="tertiary"
                  onClick={() => handleUpgradeClick()}
                >
                  {t('strategies.configureAsset.upgrade.button')}
                </Button>
              </div>
              <img
                alt="merlin"
                src="/upgradePlan.svg"
                className={'w-25 justify-self-end px-8'}
              />
            </div>
          </Card>
        </div>
      )}
    </>
  ) : (
    <Card className="bg-primary my-4" title={''}>
      {onCloseClick && (
        <Icon
          svgName="close"
          className="ml-auto cursor-pointer -my-2 dark:text-black"
          onClick={onCloseClick}
        />
      )}
      <div className="flex flex-col items-center bg-primary w-full pt-2 pb-2">
        <img alt="merlin" src="/upgradePlan.svg" className={'w-25'} />
        <h1 className="text-black text-xl font-semibold text-center mt-2">
          {t(`upgradeSubscription.${feature}.title`, { ...textVars })}
        </h1>
        <p className="mt-2 text-black text-md text-center">
          {t(`upgradeSubscription.${feature}.subtitle`, { ...textVars })}
        </p>
        <Button
          buttonClass="w-50 mt-5"
          type="button"
          variant="tertiary"
          onClick={() => handleUpgradeClick()}
        >
          {t('strategies.configureAsset.upgrade.button')}
        </Button>
      </div>
    </Card>
  );

  // return <></>;
};
